import { useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, Clock3 } from 'lucide-react';
import { Dialog, DialogDescription, DialogTitle } from 'components/ui/dialog';
import { PublicDialogContent } from 'components/ui/public-dialog';

const weekdays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
function visitorZone() { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'; }
export function calendarDays(year, month) {
  const first = new Date(year, month, 1, 12);
  const blanks = (first.getDay() + 6) % 7;
  return [
    ...Array(blanks).fill(null),
    ...Array.from({ length: new Date(year, month + 1, 0, 12).getDate() }, (_, index) => index + 1),
  ];
}
export default function BookingDialog({ open, onOpenChange, onCloseAutoFocus, selection, theme }) {
  const [zone, setZone] = useState(visitorZone);
  const [month, setMonth] = useState(() => new Date(new Date().getFullYear(), new Date().getMonth(), 1, 12));
  const zones = useMemo(() => {
    try { return [...new Set([visitorZone(), 'UTC', ...Intl.supportedValuesOf('timeZone')])]; }
    catch { return [...new Set([visitorZone(), 'UTC', 'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles', 'Europe/Belgrade'])]; }
  }, []);
  const heading = month.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  const days = calendarDays(month.getFullYear(), month.getMonth());
  function changeMonth(delta) { setMonth(current => new Date(current.getFullYear(), current.getMonth() + delta, 1, 12)); }
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <PublicDialogContent className="paperwork-booking pw-dialog public-dialog--booking" data-theme={theme} onCloseAutoFocus={onCloseAutoFocus}>
        <DialogTitle>Book your free 20-minute meeting</DialogTitle>
        <DialogDescription>
          {selection ? selection + ' · ' : ''}This optional introduction helps us discuss your paperwork needs. Choose a time when the calendar is available.
        </DialogDescription>
        <div className="pw-booking-layout">
          <div>
            <div className="pw-month-heading">
              <h3 aria-live="polite">{heading}</h3>
              <div>
                <button type="button" className="pw-icon-button" onClick={() => changeMonth(-1)} aria-label="Previous month"><ChevronLeft size={19} /></button>
                <button type="button" className="pw-icon-button" onClick={() => changeMonth(1)} aria-label="Next month"><ChevronRight size={19} /></button>
              </div>
            </div>
            <div className="pw-calendar" aria-label={heading}>
              {weekdays.map(day => <span className="pw-weekday" key={day}>{day}</span>)}
              {days.map((day, index) => day ? (
                <button type="button" key={index} disabled aria-label={month.toLocaleDateString('en-US', { month: 'long' }) + ' ' + day + ', ' + month.getFullYear() + ': availability unavailable'}>{day}</button>
              ) : <span key={index} aria-hidden="true" />)}
            </div>
            <p className="pw-calendar-notice">At least two days’ notice.</p>
          </div>
          <div className="pw-booking-times">
            <label htmlFor="pw-timezone">Your timezone</label>
            <select id="pw-timezone" value={zone} onChange={event => setZone(event.target.value)}>
              {zones.map(value => <option key={value} value={value}>{value.replaceAll('_', ' ')}</option>)}
            </select>
            <p className="pw-duration"><Clock3 size={17} aria-hidden="true" />20 minutes · Online meeting</p>
            <div className="pw-booking-message" role="status">
              <h3>Meeting times are not available yet.</h3>
              <p>Please check back when the calendar opens. No appointment has been booked.</p>
            </div>
          </div>
        </div>
      </PublicDialogContent>
    </Dialog>
  );
}
